'use client';

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserData } from '../hooks/useUserData';

interface UserDataContextType {
  userDetails: any;
  isUserDetailsLoading: boolean;
  isUserBootstrapFetching: boolean;
  balance: number; // Total balance (personal + team)
  personalBalance: number | undefined; // Personal balance only (undefined when not loaded)
  isBalanceLoading: boolean;
  isPersonalBalanceLoading: boolean;
  isPersonalBalanceFetching: boolean; // Whether personal balance is currently being fetched
  refetchBalance: () => Promise<any>;
  refetchPersonalBalance: () => Promise<any>;
  refreshBalance: () => Promise<any>; // Backward compatibility alias
  refetchUserDetails: () => Promise<any>;
  team: any;
  userTeams: any[];
  hasTeam: boolean; // Boolean indicating if user has a team
  isTeamOwner: boolean; // Whether the current user is the owner of their team
  isLoadingUserTeams: boolean;
  teamPreferences: any;
  isLoadingTeamPreferences: boolean;
  effectiveProducts: any[];
  effectiveDepartments: any[];
  effectiveBuyingPersona: any[];
  isAdmin: boolean;
  isEAUser: boolean;
  isSHV2User: boolean;
  hasCustomSignalsAccess: boolean;
  isWorkspaceAdmin: boolean;
}

const UserDataContext = createContext<UserDataContextType | undefined>(
  undefined,
);

const LoadedUserDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const userData = useUserData();
  return (
    <UserDataContext.Provider value={userData}>
      <TeamScopeSync />
      {children}
    </UserDataContext.Provider>
  );
};

export const UserDataProvider: React.FC<{
  children: ReactNode;
  value?: UserDataContextType;
}> = ({ children, value }) =>
  value ? (
    <UserDataContext.Provider value={value}>
      <TeamScopeSync />
      {children}
    </UserDataContext.Provider>
  ) : (
    <LoadedUserDataProvider>{children}</LoadedUserDataProvider>
  );

export const useUserDataContext = (): UserDataContextType => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error(
      'useUserDataContext must be used within a UserDataProvider',
    );
  }
  return context;
};

// Safe version that returns null instead of throwing - useful for edge cases like logout
export const useUserDataContextSafe = (): UserDataContextType | null => {
  const context = useContext(UserDataContext);
  return context || null;
};

function TeamScopeSync() {
  const { userDetails, team } = useUserDataContext();
  const queryClient = useQueryClient();
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key !== 'clearitty:team-context-changed' || !event.newValue)
        return;
      try {
        const change: unknown = JSON.parse(event.newValue);
        if (
          !change ||
          typeof change !== 'object' ||
          !('userId' in change) ||
          !('teamId' in change)
        )
          return;
        if (
          change.userId !== String(userDetails?.id) ||
          typeof change.teamId !== 'string' ||
          change.teamId === team?.id
        )
          return;
        void queryClient.cancelQueries().finally(() => {
          queryClient.clear();
          window.location.reload();
        });
      } catch {
        return;
      }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [queryClient, userDetails?.id, team?.id]);
  return null;
}
